import React, { useCallback, useEffect, useRef } from 'react';
import { Box, Stack } from '@atlaskit/primitives/compiled';
import ProgressBar from '@atlaskit/progress-bar';
import { SuccessFlag } from '@atlassian/jira-flags';
import { useIntl } from '@atlassian/jira-intl';
import type { ExportIssuesDownloadError } from '../../types.tsx';
import messages from './messages.tsx';
import { downloadFile } from './utils.tsx';

type Props = {
	id: string;
	downloadUrl: string;
	fileExtension: string;
	onDismissed: () => void;
	onComplete: () => void;
	onError: (error: ExportIssuesDownloadError) => void;
};

export const DownloadFlag = ({
	id,
	downloadUrl,
	fileExtension,
	onDismissed,
	onComplete,
	onError,
}: Props) => {
	const { formatMessage } = useIntl();
	const abortController = useRef(new AbortController());

	const handleOnDismissed = useCallback(() => {
		abortController.current.abort();
		onDismissed();
	}, [onDismissed]);

	useEffect(() => {
		const { signal } = abortController.current;

		downloadFile({ downloadUrl, signal })
			.then(() => {
				onComplete();
			})
			.catch((error: ExportIssuesDownloadError) => {
				/**
				 * We only want to propagate the error if it's not an abort error (triggered byt the user dismissing the flag)
				 */
				if (error.name !== 'AbortError') {
					onError(error);
				}
			});

		// We only want to trigger the download once
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<SuccessFlag
			id={id}
			messageId="issue-navigator-action-export-issues.common.ui.download-flag.success-flag"
			messageType="transactional"
			title={formatMessage(messages.downloadingCsvFile, {
				fileExtension: fileExtension.toUpperCase(),
			})}
			description={
				<Box paddingInlineEnd="space.400">
					<Box paddingBlockStart="space.100">
						<Stack space="space.100">
							<ProgressBar
								isIndeterminate
								ariaLabel={formatMessage(messages.downloadingProgressBarAriaLabel)}
							/>
						</Stack>
					</Box>
				</Box>
			}
			onDismissed={handleOnDismissed}
		/>
	);
};
